import type { PendingShopifyIntegrationCookie } from "schemas/cookies";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { useSubscriptionTier } from "@v2/hooks/subscription";
import { useShopifyIntegrationConnect } from "@v2/hooks/useShopifyIntegrationConnect";
import { ArrowUpRight } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type DesignPattern = "inline" | "popover" | "modal";

const DESIGN_PATTERN_MAP: Record<string, DesignPattern> = {
  shopStyles: "inline",
  publish: "popover",
  preview: "popover",
  analytics: "modal",
  projectView: "modal",
  shopSettingsIntegration: "modal",
};

const getDesignPattern = (
  cookieType: PendingShopifyIntegrationCookie["type"],
): DesignPattern => {
  return DESIGN_PATTERN_MAP[cookieType] ?? "inline";
};

export const ConnectShopifyCallout: React.FC<{
  cookieValue: Exclude<
    PendingShopifyIntegrationCookie,
    | { type: "onboarding" }
    | { type: "noShopifyErrorModal" }
    | { type: "projectSettings" }
    | { type: "newProject" }
  >;
  className?: string;
  onCancel?: () => void; // For popover design pattern
  suppressContent?: boolean; // To hide title/description when parent has its own
}> = ({ cookieValue, className, onCancel, suppressContent = false }) => {
  const subscriptionTier = useSubscriptionTier();
  const designPattern = getDesignPattern(cookieValue.type);

  const detailMessage = exhaustiveSwitch({ type: cookieValue.type })({
    // Note (Evan, 2024-04-09): We only tell people to upgrade their account when they are on the free plan -
    // there are other cases where an upgrade may be required (e.g. the published pages limit has been reached)
    // but the logic of figuring that out is a little annoying, so we just account for the most obvious case here.
    publish: `To publish this page, add Shopify as an integration${subscriptionTier === "free" ? " and upgrade your account" : ""}.`,
    preview: `To preview this page, add Shopify as an integration.`,
    shopStyles: `To import saved styles from your Shopify theme, add Shopify as an integration.`,
    productPicker:
      "Replo allows you to access Shopify data dynamically in the editor. To access product data, add Shopify as an integration.",
    assets: "To add images via upload, add Shopify as an integration.",
    billingModal: null,
    shopSettingsIntegration:
      "Replo will automatically sync your theme, products, and store info.",
    sections:
      "To pick a section from your Shopify theme, please connect a Shopify store to this project.",
    projectView:
      // Note (Kevin 2025-05-06): "Replo allows you to access Shopify data dynamically in the editor by connecting your Shopify store." description is handled by the ShopifyButton.tsx Popover component
      null,
    analytics:
      "This allows Replo to start collecting data across all pages in your Shopify store.",
    abtesting: null,
    home: null,
  });

  const title = exhaustiveSwitch({ type: cookieValue.type })({
    publish: "Publish Page",
    preview: "Preview Page",
    productPicker: "Add Product Data",
    assets: "Upload Images",
    billingModal: null,
    shopSettingsIntegration: "Shopify",
    sections: "Load Sections",
    shopStyles: "Import Saved Styles",
    // Note (Kevin 2025-05-06): "Connect Shopify Store" title is handled by the ShopifyButton.tsx Popover component
    projectView: null,
    analytics: "Connect Shopify to Track Analytics",
    abtesting: null,
    home: "Connect Shopify",
  });

  let imageSrc = "/images/flows/integrate-shopify.png";

  if (designPattern === "modal") {
    if (cookieValue.type === "shopSettingsIntegration") {
      imageSrc = "/images/flows/integrate-shopify-right-cropped.png";
    } else {
      imageSrc = "/images/flows/integrate-shopify-full.png";
    }
  }

  if (designPattern === "modal") {
    return (
      <div
        className={twMerge(
          "flex items-stretch justify-between -m-4",
          className,
        )}
      >
        <div className="w-65 flex flex-col gap-4 p-5 pr-1">
          {!suppressContent && title && (
            <h2 className="header-h5 text-default">{title}</h2>
          )}
          {!suppressContent && detailMessage && (
            <p className="text-sm text-muted">{detailMessage}</p>
          )}

          <ConnectShopifyIntegrationCta
            cookieValue={cookieValue}
            showEndIcon={true}
            ctaText="Add Integration"
            className="w-fit"
          />
        </div>

        <div className="relative flex-1 h-auto flex items-center justify-center -mr-18 overflow-hidden">
          {cookieValue.type === "shopSettingsIntegration" ? (
            <div className="flex items-center justify-center pointer-events-none">
              <img
                src="/images/onboarding/shopify-circle.svg"
                alt="Shopify"
                className="w-64 h-64 scale-125"
              />
              <img
                src="/images/onboarding/replo-circle.svg"
                alt="Replo"
                className="w-64 h-64 -ml-18 scale-125"
              />
            </div>
          ) : (
            <img
              src={imageSrc}
              alt="Integrate with Shopify"
              className="absolute inset-0 w-full h-full object-cover object-left select-none pointer-events-none"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "text-placeholder flex flex-col gap-4 py-2 text-sm font-normal",
        (cookieValue.type === "publish" || cookieValue.type === "preview") &&
          "px-2",
        className,
      )}
    >
      {!suppressContent && (title || detailMessage) && (
        <div className="flex flex-col gap-2">
          {title && <h2 className="text-default font-semibold">{title}</h2>}
          {detailMessage && <p className="text-xs">{detailMessage}</p>}
        </div>
      )}

      {designPattern !== "popover" && (
        <ConnectShopifyIntegrationCta
          cookieValue={cookieValue}
          showEndIcon={true}
        />
      )}

      <img
        src={imageSrc}
        alt="Integrate with Shopify"
        className="my-2 block w-full"
      />

      {designPattern === "popover" && (
        <div className="flex flex-row gap-2 items-center">
          <Button variant="secondary" size="base" onClick={onCancel}>
            Cancel
          </Button>
          <ConnectShopifyIntegrationCta
            cookieValue={cookieValue}
            size="base"
            showEndIcon={true}
          />
        </div>
      )}
    </div>
  );
};

export const ConnectShopifyIntegrationCta: React.FC<{
  cookieValue: PendingShopifyIntegrationCookie;
  size?: "sm" | "base" | "lg";
  ctaText?: string;
  showEndIcon?: boolean;
  className?: string;
  variant?: "primary" | "secondary" | "destructive" | "tertiary" | "link";
}> = ({
  cookieValue,
  size = "base",
  ctaText = "Connect Shopify",
  showEndIcon = false,
  className,
  variant = "primary",
}) => {
  const { isLoading, handleConnect } = useShopifyIntegrationConnect({
    cookieValue,
  });

  const designPattern = getDesignPattern(cookieValue.type);

  return (
    <Button
      variant={variant}
      size={size}
      UNSAFE_className={
        className ?? (designPattern === "popover" ? undefined : "w-full")
      }
      onClick={(e) => {
        void handleConnect(e);
      }}
      loading={isLoading}
      disabled={isLoading}
      endEnhancer={showEndIcon ? <ArrowUpRight /> : undefined}
    >
      {ctaText}
    </Button>
  );
};
